import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Chip, Button, Avatar, TablePagination
} from '@mui/material';
import { Refresh, Delete, Block, Person } from '@mui/icons-material';
import axios from 'axios';

const API_BASE = 'https://api.bharatdarshan.app/api/sanatani';

const BannedNames = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const fetchReports = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await axios.get(`${API_BASE}/admin/banned-names?page=${page + 1}&limit=${rowsPerPage}`);
      if (res.data.success) {
        setReports(res.data.data || []);
        setTotal(res.data.total || 0);
      }
    } catch (err) {
      setError('Failed to fetch banned name reports');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [page, rowsPerPage]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE}/admin/banned-names/${id}`);
      setSuccess('Report deleted');
      fetchReports();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to delete report');
    }
  };

  const handleBanUser = async (profileId) => {
    try {
      await axios.put(`${API_BASE}/admin/users/${profileId}/ban`);
      setSuccess('User ban status toggled');
      fetchReports();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Failed to ban user');
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          <Block sx={{ mr: 1, verticalAlign: 'middle' }} />
          Banned Name Reports
        </Typography>
        <Button variant="outlined" startIcon={<Refresh />} onClick={fetchReports}>
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5' }}>
        <Typography variant="body2" color="text.secondary">
          Total Reports: <strong>{total}</strong> — These are users who attempted to set non-Sanatani names.
        </Typography>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><strong>#</strong></TableCell>
                <TableCell><strong>User</strong></TableCell>
                <TableCell><strong>Attempted Name</strong></TableCell>
                <TableCell><strong>Phone</strong></TableCell>
                <TableCell><strong>Device</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary" sx={{ py: 3 }}>
                      No banned name reports found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                reports.map((report, index) => (
                  <TableRow key={report._id} hover>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          src={report.profile?.profileImageUrl ? `${API_BASE.replace('/api/sanatani', '')}${report.profile.profileImageUrl}` : ''}
                          sx={{ width: 32, height: 32 }}
                        >
                          <Person />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {report.profile?.name || report.user?.name || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {report.userId?.toString()?.slice(-6) || '-'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={report.attemptedName || '-'}
                        color="error"
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{report.user?.phone || '-'}</TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {report.deviceName || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {report.profile?.isBanned ? (
                        <Chip label="Banned" color="error" size="small" />
                      ) : (
                        <Chip label="Active" color="success" size="small" />
                      )}
                    </TableCell>
                    <TableCell>{formatDate(report.createdAt)}</TableCell>
                    <TableCell>
                      {report.profile && !report.profile.isBanned && (
                        <IconButton
                          color="error"
                          size="small"
                          title="Ban User"
                          onClick={() => handleBanUser(report.profile?._id || report.userId)}
                        >
                          <Block fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        color="default"
                        size="small"
                        title="Delete Report"
                        onClick={() => handleDelete(report._id)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          />
        </TableContainer>
      )}
    </Box>
  );
};

export default BannedNames;
